import './ProductDetails.scss';

import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import InputNumber from '../../component/input/InputNumber';
import { ProductService } from '../../service/ProductService';
import { MessageContext } from '../../context/MessageContext';
import { LayoutContext } from '../../context/LayoutContext';
import { useContext } from 'react';
import { ReactComponent as EditWhiteIcon } from '../../assets/images/edit-white-icon.svg';
import { ReactComponent as CopyIcon } from '../../assets/images/copy.svg';
import { ReactComponent as ImageIcon } from '../../assets/images/image.svg';
import { FormattedUtils } from '../../utils/FormattedUtils';
import LoadingOverlay from '../../component/loadingoverlay/LoadingOverlay';
import useNavigationContext from '../../utils/navigationcontext/NavigationContext';
import { useAuth } from '../../hook/useAuth';
import ProductSidebar from './ProductSidebar';
import { getI18FromName } from '../../utils/i18nUtils';
import PriceDistributionChart from './PriceDistributionChart';
import ProductComponentContainers from './ProductComponentContainers';
import { ProductInventoryService } from '../../service/warehouse';

const ProductDetails = () => {
    const { t } = useTranslation();
    const { productId } = useParams();
    const { showErrorMessage } = useContext(MessageContext);
    const { setPageTitle, setShowPageTitle, setShowBackButton, setBackButtonLabel, setBackButtonNavigateTo } = useContext(LayoutContext);
    const isMounted = useRef(true);

    const [productInfo, setProductInfo] = useState(null);
    const [loadingProductInfo, setLoadingProductInfo] = useState(false);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [isPhotoLoading, setIsPhotoLoading] = useState(false);
    const [showProductSidebar, setShowProductSidebar] = useState(false);

    const [minQty, setMinQty] = useState(0);
    const [maxQty, setMaxQty] = useState(0);
    const [price, setPrice] = useState(0);
    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(20);
    const [vatCodeOptions, setVatCodeOptions] = useState([]);

    const navigate = useNavigate();
    const { isUserInAnyRole } = useAuth();

    const navigationContext = useNavigationContext();

    const productInventoryService = new ProductInventoryService();

    const fetchVatCodeOptions = async () => {
        try {
            const codes = await productInventoryService.getVatCodeList();

            setVatCodeOptions(
                ((codes) || []).map(code => ({
                    id: code,
                    label: t(`products.vatCodes.${code}`, code),
                })),
            );
        } catch {
            showErrorMessage(t('products.error.fetchVatCodes'));
        }
    };

    useEffect(() => {
        fetchVatCodeOptions();
    }, []);

    
    const selectedVatCode = vatCodeOptions.find(
        option => option.id === productInfo?.vatCode,
    );

    const vatCodeLabel = selectedVatCode?.label || productInfo?.vatCode || '';
    
    useEffect(() => {
        isMounted.current = true;
        setShowBackButton(true);
        setBackButtonNavigateTo(`/products?${navigationContext.getContextParam()}`);
        setShowPageTitle(false);
        return () => {
            isMounted.current = false;
            setBackButtonLabel(null);
            setShowBackButton(false);
            setBackButtonNavigateTo(null);
        }
    }, []);


    useEffect(() => {
        if (!!productId) {
            fetchProductInfo();
        }
    }, [productId]);

    /// TODO cena ze statystyk
    useEffect(() => {
        if (productInfo) {
            setMinQty(productInfo.minQuantity || 0);
            setMaxQty(productInfo.maxQuantity || 0);
            const currentPrice = productInfo.price || 0;
            const currentMinPrice = productInfo.minPrice || 0;
            let currentMaxPrice = productInfo.maxPrice || 20;
            
            // If price is greater than maxPrice, increase maxPrice by 5 more than the price
            if (currentPrice > currentMaxPrice) {
                currentMaxPrice = currentPrice + 5;
            }
            
            setPrice(currentPrice);
            setMinPrice(currentMinPrice);
            setMaxPrice(currentMaxPrice);
            setBackButtonLabel(
                <>
                    <span
                        onClick={() => navigate(`/products?${navigationContext.getContextParam()}`)}
                        style={{ cursor: 'pointer' }}
                    >
                        {t('menu.products')}
                    </span>
                    <span> / </span>
                    <span>{productInfo?.name}</span>
                </>
            );
        }
    }, [productInfo]);

    const fetchProductInfo = async () => {
        try {
            setLoadingProductInfo(true);
            const productData = await ProductService.get(productId);
            if (isMounted.current) {
                setProductInfo(productData);
                setBackButtonLabel(
                    <>
                        <span
                            onClick={() => navigate(`/products?${navigationContext.getContextParam()}`)}
                            style={{ cursor: 'pointer' }}
                        >
                            {t('menu.products')}
                        </span>
                        <span> / </span>
                        <span>{productData?.name}</span>
                    </>
                );
                // Pobierz informacje o zdjęciu
                const url = ProductService.getPhotoUrl(productData);
                if (url) {
                    setIsPhotoLoading(true);
                    setPhotoUrl(url);
                    const img = new Image();
                    img.onload = () => { if (isMounted.current) setIsPhotoLoading(false); };
                    img.onerror = () => {
                        if (isMounted.current) {
                            setIsPhotoLoading(false);
                            setPhotoUrl(null); // Hide image on error
                        }
                    };
                    img.src = url;
                } else {
                    setPhotoUrl(null);
                    setIsPhotoLoading(false);
                }
            }
        } catch (error) {
            if (isMounted.current) {
                showErrorMessage(t('products.error.fetch'));
            }
        } finally {
            if (isMounted.current) {
                setLoadingProductInfo(false);
            }
        }
    };

    const handleEditClick = () => {
        setShowProductSidebar(true);
    };

    if (!productInfo) {
        return (
            <div className="product-details-container">
                <LoadingOverlay />
            </div>
        );
    }

    const includeComponentsValue =
        productInfo?.includeComponents ??
        productInfo?.include_components;

    const shouldShowComponentContainers =
        includeComponentsValue === true ||
        includeComponentsValue === 'true';


    return (
        <>
            <ProductSidebar
                visible={showProductSidebar}
                externalId={productId}
                viewMode="EDIT"
                onHide={() => setShowProductSidebar(false)}
                onSuccess={(updatedProduct) => {
                    setProductInfo(updatedProduct);
                    setShowProductSidebar(false);
                }}
            />
            <div className='product-details-page'>
                <div className='grid m-2'>
                    <div className="product-details-container col mr-2">
                        <LoadingOverlay loading={loadingProductInfo} >
                            {/* Header Section */}
                            <div className='product-info-section'>
                                <div className="header-section">
                                    <div className="product-info">
                                        <div className="product-photo">
                                            {isPhotoLoading ? (
                                                <div className="photo-loading">
                                                    <i className="pi pi-spin pi-spinner" />
                                                </div>
                                            ) : photoUrl ? (
                                                <img src={photoUrl} alt={productInfo?.name} />
                                            ) : (
                                                <div className="photo-placeholder">
                                                    <ImageIcon />
                                                </div>
                                            )}
                                        </div>
                                        <div className="product-details">
                                            <span className="product-name">{productInfo?.name}</span>
                                            <div className="product-id">
                                                <span>{productInfo?.numberId}</span>
                                                <CopyIcon className="copy-icon" />
                                            </div>
                                        </div>
                                    </div>
                                    <button className="edit-button" onClick={handleEditClick}>
                                        <EditWhiteIcon />
                                    </button>
                                </div>

                                {/* Separator */}
                                <div className="separator" />

                                {/* Main Content */}
                                <div className="main-content">
                                    {/* Left Column - Product Details */}
                                    <div className="left-column">
                                        <div className="details-list">
                                            <div className="detail-item">
                                                <span className="label">{t('productDetails.name')}</span>
                                                <span className="value">{productInfo?.name}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">{t('productDetails.id')}</span>
                                                <span className="value">{productInfo?.numberId}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">{t('productDetails.ean')}</span>
                                                <span className="value">{productInfo?.ean}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">{t('productDetails.category')}</span>
                                                <span className="value">{getI18FromName(productInfo?.category?.name) || productInfo?.category}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">{t('productDetails.weightVolume')}</span>
                                                <span className="value">{productInfo?.weightVolume || productInfo?.volume}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">{t('productDetails.udm')}</span>
                                                <span className="value">{productInfo?.udm || productInfo?.unitOfMeasurement}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">{t('productDetails.status')}</span>
                                                <span className="value">{t(`ProductStatus.${productInfo?.status}`)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Vertical Separator */}
                                    <div className="vertical-separator" />

                                    {/* Right Column - Quantity and Price */}
                                    <div className="right-column">
                                        {/* Quantity Section */}
                                        <div className="quantity-section">
                                            <div className="quantity-input">
                                                <span className="label">{t('productDetails.minQuantity')}</span>
                                                <InputNumber
                                                    value={minQty}
                                                    onValueChange={(e) => setMinQty(e.value)}
                                                    mode="decimal"
                                                    min={0}
                                                    max={maxQty}
                                                    className="quantity-field"
                                                    readOnly={true}
                                                />
                                            </div>
                                            <div className="quantity-input">
                                                <span className="label">{t('productDetails.maxQuantity')}</span>
                                                <InputNumber
                                                    value={maxQty}
                                                    onValueChange={(e) => setMaxQty(e.value)}
                                                    mode="decimal"
                                                    min={minQty}
                                                    className="quantity-field"
                                                    readOnly={true}
                                                />
                                            </div>
                                        </div>

                                        {/* Horizontal Separator */}
                                        <div className="horizontal-separator" />

                                        {/* Price Section */}
                                        <div className="price-section">
                                            <span className="label">{t('productDetails.price')}</span>
                                            <div className="price-display">
                                                <span className="current-price">{FormattedUtils.formatPrice(price, productInfo?.currency)}</span>
                                            </div>
                                            <div className="price-slider">
                                                <div className="track" />
                                                <div className="progress" style={{ width: `${maxPrice > minPrice ? ((price - minPrice) / (maxPrice - minPrice)) * 100 : 0}%` }} />
                                            </div>
                                            <div className="price-range">
                                                <span>{FormattedUtils.formatPrice(minPrice, productInfo?.currency)}</span>
                                                <span>{FormattedUtils.formatPrice(maxPrice, productInfo?.currency)}</span>
                                            </div>
                                        </div>

                                        {/* Horizontal Separator */}
                                        <div className="horizontal-separator" />

                                        {/* Meta Info Section */}
                                        <div className="meta-info">
                                            <span className="meta-item text-center">VAT code: {vatCodeLabel}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </LoadingOverlay>
                    </div >
                    <div className="product-details-container col">
                        <PriceDistributionChart 
                            productId={productId}
                            currency={productInfo?.currency || ''}
                        />
                    </div>
                </div>

                {shouldShowComponentContainers && (
                    <div className="grid m-2 product-components-section-row">
                        <div className="col">
                            <ProductComponentContainers productId={productId} />
                        </div>
                    </div>
                )}

                {/* <div className='grid m-2'>
                    <div className="product-details-container col"></div>
                </div>
                <div className='grid m-2'>
                    <div className="product-details-container col"></div>
                </div>
                <div className='grid m-2'>
                    <div className="product-details-container col"></div>
                </div> */}
            </div>
        </>
    );
};

export default ProductDetails;

